import type { BidAskPair } from '@/types/common.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';
import type { BidAskRate } from '@/utils/number-point.ts';

export interface MarginAmountComputationParameters {
  notionalCurrencyIndex: CurrencyIndex;
  nearNotional: number;
  farNotional: number;

  spot: BidAskRate;
  nearPrice: BidAskRate;
  farPrice: BidAskRate;

  traderSpotPrice: BidAskRate;
  traderNearDeltaRate: BidAskRate;
  traderFarDeltaRate: BidAskRate;

  appliedMarginSpotDeltaRate: BidAskPair;

  nearDiscountFactor: number;
  farDiscountFactor: number;

  commercialMarginAmountCurrencyIndex: CurrencyIndex;
}

/**
 * This is a version of margin swap computation
 * based on excel spreadhseet specifications
 */
// like the name of the function suggest
// and restore all other fields in rfs.ts
export function computeCommercialMarginAmount({
  notionalCurrencyIndex,
  nearNotional,
  farNotional,

  spot,
  nearPrice,
  farPrice,

  traderSpotPrice,
  traderNearDeltaRate,
  traderFarDeltaRate,

  appliedMarginSpotDeltaRate,

  nearDiscountFactor,
  farDiscountFactor,

  commercialMarginAmountCurrencyIndex,
}: MarginAmountComputationParameters): BidAskPair {
  // NearPriceTraderBid (Trader/Sales Near price)
  // =SpotPriceTraderBid+NearPointsTraderBid*10^-4
  const traderNearPrice = {
    bid: traderSpotPrice.bid + traderNearDeltaRate.bid,
    ask: traderSpotPrice.ask + traderNearDeltaRate.ask,
  };

  // FarPriceTraderBid (Trader/Sales Far price)
  // =SpotPriceTraderBid+FarPointsTraderBid*10^-4
  const traderFarPrice = {
    bid: traderSpotPrice.bid + traderFarDeltaRate.bid,
    ask: traderSpotPrice.ask + traderFarDeltaRate.ask,
  };

  const convertToCcy1 = (amounts: BidAskPair, referenceRates: BidAskRate): BidAskPair =>
    notionalCurrencyIndex === 1
      ? amounts
      : {
          bid: amounts.bid / referenceRates.bid,
          ask: amounts.ask / referenceRates.ask,
        };

  // bid =NearNotional*((NearPriceClientBid-NearPriceTraderBid)*DFNear-(SpotPriceClientBid-SpotPriceTraderBid))*SI(NotionalCurrency="EUR";1;1/NearPriceClientBid)
  // ask =NearNotional*((SpotPriceClientAsk-SpotPriceTraderAsk)-(NearPriceClientAsk-NearPriceTraderAsk)*DFNear)*SI(NotionalCurrency="EUR";1;1/NearPriceClientAsk)
  const nearMarginAmountInNotionalCurrency = convertToCcy1(
    {
      bid:
        nearNotional * ((nearPrice.bid - traderNearPrice.bid) * nearDiscountFactor - (spot.bid - traderSpotPrice.bid)),
      ask: nearNotional * (spot.ask - traderSpotPrice.ask - (nearPrice.ask - traderNearPrice.ask) * nearDiscountFactor), // nota bene : inversion regarding the bid way
    },
    nearPrice,
  );

  //bid =FarNotional*((SpotPriceClientBid-SpotPriceTraderBid)-(FarPriceClientBid-FarPriceTraderBid)*DFFar)*SI(NotionalCurrency="EUR";1;1/FarPriceClientBid)
  // ask =FarNotional*((FarPriceClientAsk-FarPriceTraderAsk)*DFFar-(SpotPriceClientAsk-SpotPriceTraderAsk))*SI(NotionalCurrency="EUR";1;1/FarPriceClientAsk)
  const farEvenMarginAmountInNotionalCurrency = convertToCcy1(
    {
      bid: farNotional * (spot.bid - traderSpotPrice.bid - (farPrice.bid - traderFarPrice.bid) * farDiscountFactor),
      ask: farNotional * ((farPrice.ask - traderFarPrice.ask) * farDiscountFactor - (spot.ask - traderSpotPrice.ask)),
    },
    farPrice,
  );

  const spotMarginAmount = {
    bid:
      appliedMarginSpotDeltaRate.bid *
      (notionalCurrencyIndex === 1
        ? Math.abs(farNotional - nearNotional)
        : Math.abs(farNotional / farPrice.bid - nearNotional / nearPrice.bid)),
    ask:
      appliedMarginSpotDeltaRate.ask *
      (notionalCurrencyIndex === 1
        ? Math.abs(farNotional - nearNotional)
        : Math.abs(farNotional / farPrice.ask - nearNotional / nearPrice.ask)),
  };

  const commercialMarginAmountInCurrency2 = {
    bid: spotMarginAmount.bid + nearMarginAmountInNotionalCurrency.bid + farEvenMarginAmountInNotionalCurrency.bid,
    ask: spotMarginAmount.ask + nearMarginAmountInNotionalCurrency.ask + farEvenMarginAmountInNotionalCurrency.ask,
  };

  // finally the commercial margin amount
  return commercialMarginAmountCurrencyIndex === 2
    ? commercialMarginAmountInCurrency2
    : {
        bid: commercialMarginAmountInCurrency2.bid / traderSpotPrice.ask,
        ask: commercialMarginAmountInCurrency2.ask / traderSpotPrice.bid,
      };
}
